import React, { useState } from "react";
import { ConfiguratorModes } from "utils/enums";
import { T } from "services/lang/LanguageService";
import { DashboardLayoutType, IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import useDashboardStore from "store/dashboard-store";
import { v4 as uuidv4 } from "uuid";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import DashboardComponentCard from "./GroupComponentCard";
import DashboardComponentConfiguratorNew from "./GroupComponentConfiguratorNew";
import { updateDashboardTabs } from "../../utils";
import MoveGroupOrComponent from "./MoveGroupOrComponent";
import { ContextMenuItem } from "../layouts/interfaces";
import GroupHeaderComponentConfigurator from "./GroupHeaderComponentConfigurator";

interface DashboardComponentProps {
  groupId: string;
  tabId: number;
  component: IDashboardComponent;
  layoutType: DashboardLayoutType;
  isGroupHeader?: boolean;
  setIsGridLayoutEditAvailable?: (isGridLayoutEditAvailable: boolean) => void;
  setCurrentContextMenuItems: (currentContextMenuItems: ContextMenuItem[]) => void;
  onCollapseChangeInGrid?: (componentId: string, isCollapsed: boolean) => void;
}

enum DialogType {
  MOVE = "MOVE",
  EDITOR = "EDITOR",
}

const DashboardComponent = ({
  groupId,
  component,
  layoutType,
  tabId,
  isGroupHeader,
  setIsGridLayoutEditAvailable,
  setCurrentContextMenuItems,
  onCollapseChangeInGrid,
}: DashboardComponentProps) => {
  const dashboard = useDashboardStore(state => state.dashboard);
  const updateComponent = useDashboardStore(state => state.updateComponent);
  const deleteComponent = useDashboardStore(state => state.removeComponent);
  const [isConfiguratorVisible, setIsConfiguratorVisible] = useState<boolean>(false);
  const [isMoveComponentVisible, setIsMoveComponentVisible] = useState<boolean>(false);

  const handleDeleteComponent = () => {
    easasConfirmDialog({
      icon: "pi pi-exclamation-triangle",
      header: T("action.confirm-action"),
      message: T("confirm-dialog.are-you-sure-you-want-to-delete-this-component"),
      confirmedCallback: () => {
        deleteComponent(groupId, component.id, tabId, isGroupHeader);
      },
    });
  };

  const duplicateComponentInfo = (components: IDashboardComponent[] | undefined, componentId: string) => {
    if (!components) return;

    const original = components.find(c => c.id === componentId);
    if (!original) return;

    const clone = JSON.parse(JSON.stringify(original));
    clone.id = uuidv4();

    if (clone.type === "Scada") {
      clone.config.id = uuidv4();
    }

    components.push(clone);
  };

  const handleDuplicate = () => {
    if (!dashboard) return;

    const currentGroup = dashboard.dashboardTabsJs?.[tabId]?.componentGroups?.find(g => g.id === groupId);

    if (!currentGroup) return;

    if (isGroupHeader) {
      duplicateComponentInfo(currentGroup.headerConfig?.components, component.id);
    } else {
      duplicateComponentInfo(currentGroup.components, component.id);
    }

    const updatedTabs = dashboard.dashboardTabsJs.map((tab, index) =>
      index === tabId
        ? {
            ...tab,
            componentGroups: tab.componentGroups.map(g => (g.id === groupId ? currentGroup : g)),
          }
        : tab
    );

    updateDashboardTabs(dashboard, updatedTabs);
  };

  const items = [
    {
      label: T("action.edit-config"),
      icon: "pi pi-pencil",
      command: () => {
        handleDialogVisibilityChange(true, DialogType.EDITOR);
      },
    },
    {
      label: T("action.duplicate-component"),
      icon: "pi pi-clone",
      command: () => {
        easasConfirmDialog({
          icon: "pi pi-clone",
          acceptClassName: "p-button-success",
          header: T("action.confirm-action"),
          message: T("confirm-dialog.are-you-sure-you-want-to-duplicate-this-component"),
          confirmedCallback: () => {
            handleDuplicate();
          },
        });
      },
    },
    {
      label: T("action.delete"),
      icon: "pi pi-times",
      command: () => {
        handleDeleteComponent();
      },
    },
    {
      label: T("action.move-component"),
      icon: "pi pi-arrow-right",
      command: () => {
        handleDialogVisibilityChange(true, DialogType.MOVE);
      },
    },
  ];

  const handleComponentChange = (newConfig: IDashboardComponent) => {
    updateComponent(groupId, component.id, newConfig, tabId);
    handleDialogVisibilityChange(false, DialogType.EDITOR);
  };

  const handleDialogVisibilityChange = (isVisible: boolean, dialogType: DialogType) => {
    if (dialogType === DialogType.MOVE) {
      setIsMoveComponentVisible(isVisible);
    } else {
      setIsConfiguratorVisible(isVisible);
    }
    if (setIsGridLayoutEditAvailable) setIsGridLayoutEditAvailable(!isVisible);
  };

  const handleOnContextMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    setCurrentContextMenuItems(items);
  };

  const setConfiguratorDialogVisibility = (isVisible: boolean) =>
    handleDialogVisibilityChange(isVisible, DialogType.EDITOR);
  return (
    <div className="h-full">
      <div className="h-full" onContextMenu={e => handleOnContextMenu(e)}>
        <DashboardComponentCard
          component={component}
          tabId={tabId}
          groupId={groupId}
          isGroupHeader={isGroupHeader}
          onCollapseChange={collapsed => onCollapseChangeInGrid?.(component.id, collapsed)}
        />
      </div>
      {isConfiguratorVisible && !isGroupHeader && (
        <DashboardComponentConfiguratorNew
          isDialogVisible={isConfiguratorVisible}
          mode={ConfiguratorModes.EDIT}
          setIsDialogVisible={setConfiguratorDialogVisibility}
          component={component}
          onComponentChange={handleComponentChange}
          layoutType={layoutType}
          tabId={tabId}
        />
      )}
      {isConfiguratorVisible && isGroupHeader && (
        <GroupHeaderComponentConfigurator
          isVisible={isConfiguratorVisible}
          setIsVisible={setConfiguratorDialogVisibility}
          mode={ConfiguratorModes.EDIT}
          component={component}
          groupId={groupId}
        />
      )}
      {isMoveComponentVisible && dashboard && (
        <MoveGroupOrComponent
          isVisible={isMoveComponentVisible}
          setIsVisible={(isVisible: boolean) => handleDialogVisibilityChange(isVisible, DialogType.MOVE)}
          component={component}
          tabId={tabId}
          groupId={groupId}
          componentId={component.id}
          dashboardId={dashboard.id}
          isComponent
          isGroupHeader
        />
      )}
    </div>
  );
};

function areEqual(prev: Readonly<DashboardComponentProps>, next: Readonly<DashboardComponentProps>): boolean {
  return (
    JSON.stringify(prev.component) === JSON.stringify(next.component) &&
    prev.groupId === next.groupId &&
    prev.layoutType === next.layoutType
  );
}

export default React.memo(DashboardComponent, areEqual);
